import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { Pill, Radio, Text } from '@iwoca/orion';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { useGetOfferButtons } from './useGetOfferButtons';
import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';

type TOfferChoiceButtonsProps = {
    formalOffers: TFormalOffers;
    selectedOfferId?: string;
    drawdownAmount: number;
    updateSelectedOffer: (offer: TFormalOffer) => void;
    interestOnlyPillPeriods: Record<number, number>;
};
type TOfferDurationType = 'shortest' | 'longest' | 'middle';

type TOfferChoiceButtonProps = {
    offer: TFormalOffer;
    isSelected: boolean;
    disabled: boolean;
    onSelect: () => void;
    interestOnlyPillPeriod: number | null;
    durationType: TOfferDurationType;
    showMiddleDescription: boolean;
    monthlyRepayment: number | null;
};

type TOfferPillsProps = {
    durationType: TOfferDurationType;
    interestOnlyPillPeriod: number | null;
    interestOnlyOfferPeriod: number | null;
    hasNoPersonalGuarantee: boolean;
};

export const OfferChoiceButtons = ({
    formalOffers,
    selectedOfferId,
    drawdownAmount,
    updateSelectedOffer,
    interestOnlyPillPeriods
}: TOfferChoiceButtonsProps) => {
    const { sortedOffers, getDurationType, repaymentByOfferId } =
        useGetOfferButtons({ formalOffers, drawdownAmount });

    return (
        <div>
            <Text className="mt-xl font-med">
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_select_title_v21"
                    defaultMessage="What's your preferred minimum monthly repayment?"
                />
            </Text>

            <Radio.Group name="offer">
                {sortedOffers.map((offer) => (
                    <OfferChoiceButton
                        key={offer.offer_id}
                        offer={offer}
                        isSelected={selectedOfferId === offer.offer_id}
                        disabled={drawdownAmount > offer.available_cash!}
                        onSelect={() => updateSelectedOffer(offer)}
                        interestOnlyPillPeriod={
                            interestOnlyPillPeriods[offer.formal_offer_id] ??
                            null
                        }
                        durationType={getDurationType(offer)}
                        showMiddleDescription={formalOffers.length < 4}
                        monthlyRepayment={
                            repaymentByOfferId.get(offer.offer_id!) ?? null
                        }
                    />
                ))}
            </Radio.Group>
        </div>
    );
};

const OfferDescription = ({
    durationType
}: {
    durationType: TOfferDurationType;
}): JSX.Element => {
    switch (durationType) {
        case 'shortest':
            return (
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_description_shortest"
                    defaultMessage="Repay quickly and minimise interest."
                />
            );
        case 'longest':
            return (
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_description_longest"
                    defaultMessage="Keeps your cash flow flexible."
                />
            );
        case 'middle':
            return (
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_description_middle"
                    defaultMessage="Balance your repayments and manage costs."
                />
            );
    }
};

const OfferPills = ({
    durationType,
    interestOnlyPillPeriod,
    interestOnlyOfferPeriod,
    hasNoPersonalGuarantee
}: TOfferPillsProps) => {
    const hasPills =
        durationType === 'shortest' ||
        durationType === 'longest' ||
        interestOnlyPillPeriod !== null ||
        interestOnlyOfferPeriod !== null ||
        hasNoPersonalGuarantee;

    if (!hasPills) return null;

    return (
        <div className="flex flex-wrap gap-s pt-s">
            {durationType === 'shortest' && (
                <Pill variant="green">
                    <FormattedMessage
                        id="_.offer_choice_buttons.lowest_total_cost_pill"
                        defaultMessage="Lowest total cost"
                    />
                </Pill>
            )}
            {durationType === 'longest' && (
                <Pill variant="green">
                    <FormattedMessage
                        id="_.offer_choice_buttons.lowest_monthly_cost_pill"
                        defaultMessage="Lowest monthly cost"
                    />
                </Pill>
            )}

            {interestOnlyPillPeriod !== null && (
                <Pill>
                    <FormattedMessage
                        id="_.interest_only.interest_only_available"
                        defaultMessage="{interestOnlyPayments} month lower repayment period available"
                        values={{
                            interestOnlyPayments: interestOnlyPillPeriod
                        }}
                    />
                </Pill>
            )}

            {interestOnlyOfferPeriod !== null && (
                <Pill>
                    <FormattedMessage
                        id="_.interest_only.including_interest_only_period"
                        defaultMessage="Including {interestOnlyPeriod} months of only repaying interest"
                        values={{
                            interestOnlyPeriod: interestOnlyOfferPeriod
                        }}
                    />
                </Pill>
            )}

            {hasNoPersonalGuarantee && (
                <Pill variant="green">
                    <FormattedMessage
                        id="_.duration_choice.radio_buttons.no_personal_guarantee_required"
                        defaultMessage="No Personal Guarantee required"
                    />
                </Pill>
            )}
        </div>
    );
};

const OfferChoiceButton = ({
    offer,
    isSelected,
    disabled,
    onSelect,
    interestOnlyPillPeriod,
    durationType,
    showMiddleDescription,
    monthlyRepayment
}: TOfferChoiceButtonProps) => {
    const isInterestOnlyOffer = getIsInterestOnlyOffer(offer);
    const interestOnlyPeriod = getFormalOfferInterestOnlyPeriod(offer)!;
    const months = getDurationInMonths(offer.duration!);

    return (
        <Radio.Root className="mt-m p-0" variant="frame">
            <Radio.Label className="flex justify-between p-m">
                <div>
                    <div className="gap-s pr-s">
                        {monthlyRepayment !== null ? (
                            <span className="font-bold">
                                <FormattedMessage
                                    id="_.duration_choice.radio_buttons.amount_per_month"
                                    defaultMessage="{amount} per month"
                                    values={{
                                        amount: (
                                            <FormattedCurrency
                                                value={monthlyRepayment}
                                            />
                                        )
                                    }}
                                />
                            </span>
                        ) : (
                            <Skeleton
                                data-testid="monthly-repayment-skeleton"
                                className="mr-s inline-block h-[20px] w-[100px] align-middle"
                            />
                        )}{' '}
                        <span className="font-reg">
                            <FormattedMessage
                                id="_.duration_choice.radio_buttons.for_up_to_months"
                                defaultMessage="for up to {months} months"
                                values={{ months }}
                            />
                        </span>
                    </div>
                    {(durationType !== 'middle' || showMiddleDescription) && (
                        <Text className="text-s text-structure-40">
                            <OfferDescription durationType={durationType} />
                        </Text>
                    )}

                    <OfferPills
                        durationType={durationType}
                        interestOnlyPillPeriod={interestOnlyPillPeriod}
                        interestOnlyOfferPeriod={
                            isInterestOnlyOffer ? interestOnlyPeriod : null
                        }
                        hasNoPersonalGuarantee={
                            offer.personal_guarantee_proportion === 0
                        }
                    />
                </div>
                <Radio.Input
                    checked={isSelected}
                    disabled={disabled}
                    onChange={onSelect}
                />
            </Radio.Label>
        </Radio.Root>
    );
};
